import { Stack } from "@mui/material";
import type {
  GridAggregationFunction,
  GridAggregationModel,
  GridEventListener,
  GridCallbackDetails,
  GridColDef,
  GridDataSource,
  GridPinnedColumnFields,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo } from "react";
import IntercomLauncherDependantElement from "@entrilia/shared/components/IntercomLauncherDependantElement.tsx";
import DataGrid from "@entrilia/shared/components/grid/DataGrid.tsx";
import { stopEventPropagation } from "../../../../utilities/stopEventPropagation.ts";

import type { DrillDownRow } from "../../../../../../../../api/types/svTypes.ts";
import { getBiDataGridStyles } from "../../../../utilities/Utilities.tsx";
import ReportRefreshError from "../../../common/ReportRefreshError.tsx";
import ReportLoadingOverlay from "../../../common/ReportLoadingOverlay.tsx";
import useGridColumnWidths from "../../../../hooks/useGridColumnWidths.ts";
import { LIST_PAGE_SIZE } from "../utils/listGridHelper.ts";

interface Props {
  columns: GridColDef<DrillDownRow>[];
  loading: boolean;
  resultId: string | undefined;
  error: string | undefined;
  dataSource: GridDataSource;
  sortModel: GridSortModel;
  aggregationModel: GridAggregationModel;
  pinnedColumns: GridPinnedColumnFields;
  onSortingChanged: (model: GridSortModel, details: GridCallbackDetails) => void;
}

const custom: GridAggregationFunction<number, number> = {
  label: "",
  apply: () => 0,
  columnTypes: ["string"],
};
const aggregationFunctions: Record<string, GridAggregationFunction<number, number>> = {
  custom: custom,
};

const paginationModel = { page: 0, pageSize: LIST_PAGE_SIZE };
const emptyOverlay = () => null;
const sortingOrder: ("asc" | "desc")[] = ["asc", "desc"];

const ListGrid = ({ columns, loading, resultId, error, ...props }: Props) => {
  const { columnWidths, handleColumnWidthChange } = useGridColumnWidths();
  const gridColumns = useMemo(
    () => columns.map((column) => ({ ...column, width: columnWidths.get(column.field) ?? column.width })),
    [columns, columnWidths]
  );

  return (
    <IntercomLauncherDependantElement mode="opacity">
      <Stack sx={{ position: "relative", flex: 1 }} onDrop={stopEventPropagation}>
        <ReportRefreshError error={error} hasPreviousResult={resultId !== undefined} />
        {/* A new paging state prevents MUI from reusing a deep offset from the previous report. */}
        <ListGridContent
          key={resultId ?? "initial"}
          {...props}
          columns={gridColumns}
          loading={loading}
          onColumnWidthChange={handleColumnWidthChange}
        />
        {loading && <ReportLoadingOverlay />}
      </Stack>
    </IntercomLauncherDependantElement>
  );
};

interface ContentProps extends Omit<Props, "resultId" | "error"> {
  onColumnWidthChange: GridEventListener<"columnWidthChange">;
}

const ListGridContent = ({
  columns,
  loading,
  dataSource,
  sortModel,
  aggregationModel,
  pinnedColumns,
  onSortingChanged,
  onColumnWidthChange,
}: ContentProps) => {
  const apiRef = useGridApiRef();
  const gridSlots = useMemo(
    () => ({
      loadingOverlay: loading ? emptyOverlay : ReportLoadingOverlay,
      ...(loading ? { noRowsOverlay: emptyOverlay } : {}),
    }),
    [loading]
  );
  const handleSortingChanged = useCallback(
    (model: GridSortModel, details: GridCallbackDetails) => {
      if (model.length === 0 && apiRef.current?.getSortModel().length !== 0) {
        return;
      }
      onSortingChanged(model, details);
    },
    [apiRef, onSortingChanged]
  );

  return (
    <DataGrid<DrillDownRow>
      lazyLoading
      sortingMode="server"
      paginationModel={paginationModel}
      dataSource={dataSource}
      apiRef={apiRef}
      columns={columns}
      onColumnWidthChange={onColumnWidthChange}
      sortModel={sortModel}
      onSortModelChange={handleSortingChanged}
      showColumnVerticalBorder
      rowHeight={32}
      columnHeaderHeight={32}
      selectableRows={false}
      sx={getBiDataGridStyles}
      sortingOrder={sortingOrder}
      aggregationModel={aggregationModel}
      aggregationFunctions={aggregationFunctions}
      pinnedColumns={pinnedColumns}
      slots={gridSlots}
    />
  );
};

export default ListGrid;
